<script>
import HeaderWithTitleAndIcon from "@/components/HeaderWithTitleAndIcon.vue";
import {mapActions, mapGetters} from "vuex";
import {getAdminBlogListApi, deleteBlogApi} from "@/api/adminBlogApi";
import {formatTime} from "@/utils/timeFormatting";

const STATUS_TABS = [
  {label: 'All', value: ''},
  {label: 'Draft', value: 'draft'},
  {label: 'Published', value: 'published'},
  {label: 'Archived', value: 'archived'}
]

export default {
  name: 'AdminBlogListView',
  components: {HeaderWithTitleAndIcon},
  data () {
    return {
      statusTabs: STATUS_TABS,
      activeStatus: '',
      blogs: [],
      loading: false
    }
  },
  created () {
    this.fetchBlogs()
  },
  methods: {
    ...mapActions('Auth', ['logoutAction']),
    async fetchBlogs () {
      this.loading = true
      try {
        const params = {limit: 100}
        if (this.activeStatus) {
          params.status = this.activeStatus
        }
        const res = await getAdminBlogListApi(params)
        this.blogs = (res.data.items || []).map(item => ({
          ...item,
          formattedPostedAt: formatTime(item.postedAt)
        }))
      } catch (error) {
        if (error.code === 401 || error.code === 403) {
          await this.$router.push('/admin/login')
        }
      } finally {
        this.loading = false
      }
    },
    selectStatus (value) {
      this.activeStatus = value
      this.fetchBlogs()
    },
    async handleDelete (blog) {
      if (!window.confirm(`確定要刪除《${blog.title}》嗎？此動作無法復原。`)) {
        return
      }
      try {
        await deleteBlogApi(blog.id)
        await this.fetchBlogs()
      } catch (error) {
        if (error.code === 401 || error.code === 403) {
          await this.$router.push('/admin/login')
        }
      }
    },
    async handleLogout () {
      this.logoutAction()
      await this.$router.push('/admin/login')
    }
  },
  computed: {
    ...mapGetters('Auth', ['username'])
  }
}
</script>

<template>
  <div class="admin-blog-list">
    <header-with-title-and-icon>
      <template #header-title>
        Admin
      </template>
    </header-with-title-and-icon>

    <div class="toolbar">
      <div class="status-tabs">
        <button
            v-for="tab in statusTabs"
            :key="tab.value"
            :class="{active: activeStatus === tab.value}"
            @click="selectStatus(tab.value)"
        >
          {{ tab.label }}
        </button>
      </div>
      <div class="actions">
        <span class="username" v-if="username">{{ username }}</span>
        <router-link to="/admin/new" class="btn btn-primary">+ New Post</router-link>
        <button class="btn" @click="handleLogout">Logout</button>
      </div>
    </div>

    <p v-if="loading">Loading...</p>
    <p v-else-if="blogs.length === 0">No posts.</p>

    <table v-else class="blog-table">
      <thead>
      <tr>
        <th>Title</th>
        <th>Status</th>
        <th>Posted At</th>
        <th>Tags</th>
        <th></th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="blog in blogs" :key="blog.id">
        <td>{{ blog.title }}</td>
        <td>
          <span class="status-badge" :class="'status-' + blog.status">{{ blog.status }}</span>
        </td>
        <td>{{ blog.formattedPostedAt }}</td>
        <td>{{ (blog.tags || []).join(', ') }}</td>
        <td class="row-actions">
          <router-link :to="`/admin/edit/${blog.id}`" class="btn btn-small">Edit</router-link>
          <button class="btn btn-small btn-danger" @click="handleDelete(blog)">Delete</button>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped lang="less">
.admin-blog-list {
  max-width: 900px;
  margin: 20px auto;
  padding: 0 20px;

  .toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 20px;

    .status-tabs {
      display: flex;
      gap: 8px;

      button {
        padding: 0.4rem 0.8rem;
        border: 1px solid var(--gray);
        border-radius: 3px;
        background: transparent;
        color: var(--gray);
        cursor: pointer;

        &.active {
          background-color: var(--blue);
          border-color: var(--blue);
          color: var(--white);
        }
      }
    }

    .actions {
      display: flex;
      align-items: center;
      gap: 10px;

      .username {
        color: var(--gray);
        font-size: 0.9rem;
      }
    }
  }

  .btn {
    display: inline-block;
    padding: 0.4rem 0.9rem;
    border: 1px solid var(--gray);
    border-radius: 3px;
    background: transparent;
    color: var(--gray);
    text-decoration: none;
    cursor: pointer;
    font-size: 0.95rem;

    &:hover {
      color: var(--blue);
      border-color: var(--blue);
    }
  }

  .btn-primary {
    background-color: var(--blue);
    border-color: var(--blue);
    color: var(--white);

    &:hover {
      background-color: var(--blueHover);
      color: var(--white);
    }
  }

  .btn-danger:hover {
    color: var(--red);
    border-color: var(--red);
  }

  .btn-small {
    padding: 0.25rem 0.6rem;
    font-size: 0.85rem;
  }

  .blog-table {
    width: 100%;
    border-collapse: collapse;

    th, td {
      text-align: left;
      padding: 0.6rem;
      border-bottom: 1px solid var(--gray);
    }

    .row-actions {
      display: flex;
      gap: 8px;
    }

    .status-badge {
      padding: 0.1rem 0.5rem;
      border-radius: 3px;
      font-size: 0.85rem;
      color: var(--white);

      &.status-draft {
        background-color: var(--gray);
      }

      &.status-published {
        background-color: var(--green);
      }

      &.status-archived {
        background-color: var(--red);
      }
    }
  }
}

@media (max-width: 768px) {
  .admin-blog-list {
    .blog-table {
      display: block;
      overflow-x: auto;
    }
  }
}
</style>
